import {Component, React, observer, resolveCss, voidFn} from '$src/handy'
import * as css from './TheMap.css'
import {withGoogleMap, withScriptjs, GoogleMap, Marker} from 'react-google-maps'
// @ts-ignore
import {DrawingManager} from 'react-google-maps/lib/components/drawing/DrawingManager'
import deepEqual from 'deep-equal'
import DismissIcon from 'react-icons/lib/fa/close'
import {debounce} from 'lodash'
// @ts-ignore
import {MarkerClusterer} from 'react-google-maps/lib/components/addons/MarkerClusterer'
import map from 'lodash/map'
import Resizer from './TheMap/Resizer'

type MarkerType = {location: {long: number; lat: number}; _id: number | string}
type ClusteredMarker = {
  items: Record<any, MarkerType>
  numberOfMarkers: number
  firstMarker: MarkerType
  ids: {[id: string]: true}
}

export type Point = {lat: number; long: number}

export type Circle = {
  dataType: 'premise' | 'rent'
  type: 'Circle'
  c: Point
  radius: number
}
export type Polygon = {type: 'Polygon'; points: Point[]}
export type Shape = Circle | Polygon

export type DrawingState = Circle | Polygon | null

interface Props {
  css?: any
  markers: Array<MarkerType>
  setHoveredItemIds: (ids: {[id: number]: true}) => void
  hoveredItemIds: {[id: number]: true}
  setActiveItemIds: (ids: {[id: number]: true}) => void
  selectedItemIds: {[id: number]: boolean}
  activeItemIds: {[id: number]: true}
  withDrawings?: boolean
  shape?: DrawingState
  setShape?: Function
  blueMarkerLocation?: Point
  highlightSelectedItems?: boolean
  withResizer?: boolean
  shapeEditable?: boolean
}

interface State {}

const circleOptions = {
  fillColor: `rgba(255, 0, 0, 0.4)`,
  strokeColor: `rgba(255, 0, 0, 0.9)`,
  strokeWeight: 1,
  clickable: true,
  zIndex: 1,
}

const polygonOptions = circleOptions

// @ts-ignore
@withScriptjs
// @ts-ignore
@withGoogleMap
@observer
class TheMap_ extends Component<Props, State> {
  shape: null | {type: 'Circle' | 'Polygon'; ref: any}
  map: any
  polygonChangedDebounced: Function

  constructor(props: Props, context: any) {
    super(props, context)
    this.state = {}
    this.shape = null
    this.map = null

    this.polygonChangedDebounced = debounce(this.polygonChanged, 10)
  }

  setMapRef = (map: any) => {
    if (!map) return
    this.map = map.context.__SECRET_MAP_DO_NOT_USE_OR_YOU_WILL_BE_FIRED // we actually have a legitimate reason to use this

    this.applyPropsToShape()
    this.fitBounds()
  }

  fitBounds() {
    const map = this.map
    if (!map) return
    const bounds = new google.maps.LatLngBounds()

    if (this.props.markers) {
      this.props.markers.forEach(item => {
        bounds.extend({lat: item.location.lat, lng: item.location.long})
      })
    } else {
      return null
    }

    if (this.props.blueMarkerLocation) {
      bounds.extend(ourLocationToGoogleLocation(this.props.blueMarkerLocation))
    }
    if (this.shape) {
      if (this.shape.type === 'Circle') {
        bounds.union(this.shape.ref.getBounds())
      } else {
        this.shape.ref
          .getPaths()
          .getArray()[0]
          .getArray()
          .forEach(a => {
            bounds.extend(a)
          })
      }
    }
    this.map.fitBounds(bounds)
  }

  onCircleComplete = (ref: any) => {
    if (this.shape) {
      this.shape.ref.setMap(null)
      this.shape = null
    }

    this.setupListenersOnCircle(ref)
    this.shape = {type: 'Circle', ref}
    this.updatePropsFromCircleRef(ref)
  }

  onPolygonComplete = (ref: any) => {
    if (this.shape) {
      this.shape.ref.setMap(null)
      this.shape = null
    }

    this.setupListenersOnPolygon(ref)
    this.shape = {type: 'Polygon', ref}
    this.updatePropsFromPolygonRef(ref)
  }

  UNSAFE_componentDidUpdate() {
    this.applyPropsToShape()
  }

  applyPropsToShape() {
    const {props} = this

    if (props.shape == null) {
      this.discardShapeRef()
    } else if (props.shape.type === 'Circle') {
      if (this.shape && this.shape.type !== 'Circle') {
        this.discardShapeRef()
      }

      const ref =
        this.shape && this.shape.type === 'Circle'
          ? this.shape.ref
          : this.makeCircle()

      this.shape = {type: 'Circle', ref}
      this.applyPropsToCircle(props.shape, ref)
    } else {
      if (this.shape && this.shape.type !== 'Polygon') {
        this.discardShapeRef()
      }

      const ref =
        this.shape && this.shape.type === 'Polygon'
          ? this.shape.ref
          : this.makePolygon()

      this.shape = {type: 'Polygon', ref}
      this.applyPropToPolygon(props.shape, ref)
    }
  }

  UNSAFE_componentWillUpdate() {
    setTimeout(() => {
      this.applyPropsToShape()
    })
  }

  applyPropsToCircle(shape: Circle, ref: any) {
    // debugger
    ref.setOptions({
      ...circleOptions,
      editable: this.props.shapeEditable,
      center: ourLocationToGoogleLocation(shape.c),
      radius: shape.radius,
    })
  }

  applyPropToPolygon(shape: Polygon, ref: any) {
    ref.setPaths(shape.points.map(ourLocationToGoogleLocation))
    ref.setOptions({...polygonOptions, editable: this.props.shapeEditable})
  }

  makeCircle() {
    const ref = this.setupListenersOnCircle(
      new google.maps.Circle({
        ...circleOptions,
        editable: this.props.shapeEditable,
      }),
    )
    ref.setMap(this.map)
    return ref
  }

  makePolygon() {
    const ref = this.setupListenersOnCircle(
      new google.maps.Polygon({
        ...polygonOptions,
        editable: this.props.shapeEditable,
      }),
    )
    ref.setMap(this.map)
    return ref
  }

  setupListenersOnCircle = (ref: any) => {
    google.maps.event.addListener(
      ref,
      'center_changed',
      this.circleCenterChanged,
    )
    google.maps.event.addListener(
      ref,
      'radius_changed',
      this.circleRadiusChanged,
    )
    return ref
  }

  setupListenersOnPolygon = (ref: any) => {
    google.maps.event.addListener(ref, 'click', this.polygonChangedDebounced)
    google.maps.event.addListener(ref, 'dragend', this.polygonChangedDebounced)
    google.maps.event.addListener(ref, 'mouseup', this.polygonChangedDebounced)
    return ref
  }

  circleCenterChanged = () => {
    const ref = (this.shape as any).ref
    const propsShape = this.props.shape
    if (!propsShape || propsShape.type !== 'Circle') {
      this.updatePropsFromCircleRef(ref)
      return
    }

    const newCenter = googleLocationToOurLocation(ref.getCenter())
    if (
      newCenter.lat !== propsShape.c.lat ||
      newCenter.long !== propsShape.c.long
    ) {
      this.updatePropsFromCircleRef(ref)
      return
    }
  }

  circleRadiusChanged = () => {
    const ref = (this.shape as any).ref
    const propsShape = this.props.shape
    if (!propsShape || propsShape.type !== 'Circle') {
      this.updatePropsFromCircleRef(ref)
      return
    }

    if (ref.getRadius() !== propsShape.radius) {
      this.updatePropsFromCircleRef(ref)
      return
    }
  }

  polygonChanged = () => {
    const ref = (this.shape as any).ref
    const propsShape = this.props.shape
    if (!propsShape || propsShape.type !== 'Polygon') {
      this.updatePropsFromPolygonRef(ref)
      return
    }

    const newPoly = googlePolygonToOurPolygon(ref)

    if (!deepEqual(newPoly, propsShape, {strict: true})) {
      this.updatePropsFromPolygonRef(ref)
      return
    }
  }

  private discardShapeRef() {
    const {shape} = this
    if (shape) {
      shape.ref.setMap(null)
      google.maps.event.clearInstanceListeners(shape.ref)
      this.shape = null
    }
  }

  updatePropsFromCircleRef(ref: any) {
    if (!this.props.shapeEditable) return
    const circle = googleCircleToOurCircle(ref)
    if (typeof circle.radius !== 'number') return
    // debugger
    // @ts-ignore
    this.props.setShape(circle)
  }

  updatePropsFromPolygonRef(ref: any) {
    if (!this.props.shapeEditable) return
    const polygon = googlePolygonToOurPolygon(ref)

    // @ts-ignore
    this.props.setShape(polygon)
  }

  render() {
    const {props} = this
    const classes = resolveCss(css, props.css)
    const width = this.props.withResizer ? this.store.general.mapWidth() : 400

    return (
      <div {...classes('container')} style={{width: width + 'px'}}>
        {props.withResizer && (
          <div {...classes('resizerWrapper')}>
            <Resizer />
          </div>
        )}
        {props.withDrawings && props.shapeEditable && (
          <DrawingManager
            onCircleComplete={this.onCircleComplete}
            onPolygonComplete={this.onPolygonComplete}
            defaultOptions={{
              drawingControl: true,
              drawingControlOptions: {
                position: google.maps.ControlPosition.TOP_CENTER,
                drawingModes: [
                  google.maps.drawing.OverlayType.CIRCLE,
                  google.maps.drawing.OverlayType.POLYGON,
                ],
              },
              circleOptions,
              polygonOptions,
            }}
          />
        )}
        {this.renderDrawingExtras()}
        <GoogleMap
          defaultZoom={1}
          defaultCenter={{lat: -34.397, lng: 150.644}}
          ref={this.setMapRef}
          options={{scaleControl: true}}
        >
          {this.renderMarkers(props)}

          {props.blueMarkerLocation && (
            <MarkerWithColor
              color="green"
              key="mainMarker"
              position={ourLocationToGoogleLocation(props.blueMarkerLocation)}
              clickable={false}
              title="Target Housing Corporation"
            />
          )}
        </GoogleMap>
      </div>
    )
  }

  _handleMouseOverCluster = (clusterer: any) => {
    const ids = {}
    clusterer.getMarkers().forEach(m => {
      ids[m.itemId] = true
    })
    this.props.setHoveredItemIds(ids)
  }

  removeShape = () => {
    this.props.setShape(null)
  }

  private renderDrawingExtras() {
    const {shape, shapeEditable} = this.props
    const classes = resolveCss(css, this.props.css)

    if (!shape) return null

    return (
      <div {...classes('drawingExtras', !shapeEditable && 'nonEditable')}>
        {shapeEditable && (
          <div {...classes('deleteShapeIcon')} onClick={this.removeShape}>
            <DismissIcon />
          </div>
        )}

        {shape.type === 'Circle' && (
          <div
            {...classes('theRadius')}
            onClick={shapeEditable ? this._showRadiusPrompt : voidFn}
          >
            {shape.radius.toFixed(0)}m
          </div>
        )}
      </div>
    )
  }

  _showRadiusPrompt = () => {
    const {shape} = this.props
    if (!shape || shape.type !== 'Circle') return

    const newRadiusInString = window.prompt(
      'Enter the new radius (in meters)',
      String(shape.radius),
    ) as string
    if (newRadiusInString === null) {
      return
    }
    const possibleNewRadius = parseFloat(newRadiusInString)

    if (
      typeof possibleNewRadius === 'number' &&
      !isNaN(possibleNewRadius) &&
      possibleNewRadius >= 1 &&
      possibleNewRadius <= 5000
    ) {
    } else {
      alert(
        `"${newRadiusInString}" is not a valid radius. Enter a number between 1 and 5000.`,
      )
    }
    const newShape = {...shape}
    newShape.radius = possibleNewRadius
    this.props.setShape!(newShape)
  }

  private renderMarkers(
    props: Readonly<{children?: React.ReactNode}> & Readonly<Props>,
  ): React.ReactNode {
    const clusteredMarkersByLocation: Record<string, ClusteredMarker> = {}
    if (props.markers) {
      props.markers.forEach(item => {
        const loc = JSON.stringify(item.location)
        const cluster: ClusteredMarker = clusteredMarkersByLocation[loc] || {
          items: {},
          numberOfMarkers: 0,
          firstMarker: item,
          ids: {},
        }
        clusteredMarkersByLocation[loc] = cluster
        cluster.items[item._id] = item
        cluster.numberOfMarkers++
        cluster.ids[item._id] = true
      })
    } else {
      return null
    }

    return map(clusteredMarkersByLocation, (c: ClusteredMarker, i) => {
      let anyOfTheMarkersAreSelected = false
      if (props.highlightSelectedItems) {
        for (const selectedMarkerId in props.selectedItemIds) {
          if (c.ids[selectedMarkerId] === true) {
            anyOfTheMarkersAreSelected = true
            break
          }
        }
      }

      let anyOfTheMarkersAreHovered = false
      for (const hoveredMarkerId in props.hoveredItemIds) {
        if (c.ids[hoveredMarkerId] === true) {
          anyOfTheMarkersAreHovered = true
          break
        }
      }

      const anyOfTheMarkersAreActive = !!Object.keys(props.activeItemIds).find(
        activeItemId => c.ids[activeItemId] === true,
      )

      return (
        <MarkerWithColor
          noRedraw={true}
          color={
            props.highlightSelectedItems === true &&
            anyOfTheMarkersAreSelected !== true
              ? 'grey'
              : '#188FFB'
          }
          key={`marker-${i}`}
          position={{
            lat: c.firstMarker.location.lat,
            lng: c.firstMarker.location.long,
          }}
          onClick={() => {
            props.setActiveItemIds(c.ids)
          }}
          onMouseOver={() => {
            props.setHoveredItemIds(c.ids)
          }}
          title={String(c.numberOfMarkers)}
          labelClass={css.markerLabelClass}
          opacity={
            anyOfTheMarkersAreActive ? 1 : anyOfTheMarkersAreHovered ? 1 : 0.5
          }
        />
      )
    })
  }
}

const MarkerWithColor = ({color, ...rest}: {color: string} & any) => {
  return (
    <Marker
      {...rest}
      icon={{
        fillColor: color,
        fillOpacity: 1,
        strokeOpacity: 0,
        scale: 0.8,
        anchor: {x: 25, y: 50},
        path: `M25 0c-8.284 0-15 6.656-15 14.866 0 8.211 15 35.135 15 35.135s15-26.924 15-35.135c0-8.21-6.716-14.866-15-14.866zm-.049 19.312c-2.557 0-4.629-2.055-4.629-4.588 0-2.535 2.072-4.589 4.629-4.589 2.559 0 4.631 2.054 4.631 4.589 0 2.533-2.072 4.588-4.631 4.588z`,
      }}
    />
  )
}

const googleLocationToOurLocation = (l: any): Point => {
  return {
    lat: l.lat(),
    long: l.lng(),
  }
}

const googleCircleToOurCircle = (ref: any): Circle => {
  return {
    type: 'Circle',
    radius: ref.getRadius(),
    c: googleLocationToOurLocation(ref.getCenter()),
  }
}

const googlePolygonToOurPolygon = (ref: any): Polygon => {
  const paths = ref
    .getPaths()
    .getArray()[0]
    .getArray()

  const points: Point[] = paths.map(googleLocationToOurLocation)

  return {
    type: 'Polygon',
    points,
  }
}

const ourLocationToGoogleLocation = (loc: Point): any => ({
  lat: loc.lat,
  lng: loc.long,
})

const TheMap = (props: Props) => (
  <TheMap_
    {...props}
    // @ts-ignore
    googleMapURL={`https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,symbol,places&key=${process.env.REACT_APP_GOOGLE_API_KEY}`}
    loadingElement={<div style={{height: '100%'}} />}
    containerElement={<div style={{height: '100%'}} />}
    mapElement={<div style={{height: '100%'}} />}
  />
)

export default TheMap



// WEBPACK FOOTER //
// ./src/housingCorporations/TheMap.tsx